import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getActiveBackendUrl, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [retryHint, setRetryHint] = useState(null); // e.g. "Retrying (2/3)…"
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Warm-up ping: as soon as the login page loads, hit /api/health to wake
  // up the backend so the first login POST doesn't cold-start (which is the
  // #1 cause of the "Server temporarily unavailable" toast in production).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/health`, { timeout: 8000 });
      } catch {
        // Silent: if warm-up fails the actual login attempt will retry.
        if (cancelled) return;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason') || sessionStorage.getItem('logout_reason');
    if (reason === 'session_revoked' && !window.__sessionRevokedToastShown) {
      // React StrictMode mounts effects twice in dev — guard so we don't fire
      // the toast twice (and so the timer below isn't cancelled by the first
      // unmount). Survives the double-mount cycle.
      window.__sessionRevokedToastShown = true;
      sessionStorage.removeItem('logout_reason');
      if (params.get('reason')) {
        window.history.replaceState({}, '', '/login');
      }
      setTimeout(() => {
        toast.error('You were disconnected by a supervisor. Please log in again.', { duration: 6000 });
      }, 250);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRetryHint(null);

    try {
      const user = await login(email, password, rememberMe, (attempt) => {
        // Called from App.login() between retries so the button reflects progress
        setRetryHint(`Connection slow — retrying (${attempt}/3)…`);
      });
      toast.success('Login successful!');
      
      // Admin-driver: redirect based on default_view preference
      if (user.role === 'admin-driver') {
        const defaultView = user.default_view || 'admin';
        navigate(defaultView === 'driver' ? '/driver' : '/admin');
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/driver');
      }
    } catch (error) {
      console.error('Login error:', error);

      const status = error.response?.status;
      const code = error.code;

      if (status === 429) {
        toast.error('Too many login attempts. Please wait 1 minute and try again.');
      } else if (status === 401 || status === 403) {
        toast.error('Invalid email or password.');
      } else if (!error.response || code === 'ECONNABORTED' || status === 0 || status === 504 || status === 524) {
        // Cold start / slow network / Cloudflare origin timeout — actionable
        toast.error("Slow connection — the server is waking up. Wait 10 seconds and press Sign in again.", { duration: 7000 });
      } else if (status === 502 || status === 503 || status === 520 || status === 521 || status === 522) {
        toast.error('Server temporarily unavailable. Please try again in a moment.', { duration: 6000 });
      } else if (status >= 500) {
        toast.error('Server error. Please try again shortly.');
      } else {
        toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
      setRetryHint(null);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      <div className="absolute top-4 right-4 z-50">
        <LanguageSelector />
      </div>
      <div className="flex-1 flex items-center justify-center p-8 bg-blue-50">
        <div className="w-full max-w-md">
          
          <div className="mb-8 flex flex-col items-center">
            <img 
              src="/logo.jpg" 
              alt="Outsiders Travel" 
              className="h-32 w-auto object-contain mb-4"
            />
            <h1 className="text-4xl font-bold text-black mb-2" data-testid="login-title">
              {t('common.outsidersTravel')}
            </h1>
            <p className="text-gray-700 text-lg">{t('auth.manageTransfers')}</p>
          </div>

          <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-8">
            <h2 className="text-2xl font-semibold mb-6 text-black" data-testid="login-form-title">
              {t('auth.login')}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">
                  {t('auth.email')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  className="h-11 rounded-sm border-blue-200 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-700 font-medium mb-2 block">
                  {t('auth.password')}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.passwordPlaceholder')}
                    required
                    noUpperCase={true}
                    className="h-11 rounded-sm border-blue-200 focus:ring-2 focus:ring-sky-500 focus:border-transparent pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    data-testid="toggle-password-visibility"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="remember-me" 
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  data-testid="remember-me-checkbox"
                />
                <Label 
                  htmlFor="remember-me" 
                  className="text-sm text-gray-600 cursor-pointer"
                >
                  Remember me for 30 days
                </Label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-black hover:bg-blue-900 text-white font-medium rounded-sm transition-all"
                data-testid="login-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {retryHint || t('common.loading')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <LogIn className="mr-2 h-5 w-5" />
                    {t('auth.login')}
                  </span>
                )}
              </Button>
              
              <div className="text-center">
                <a 
                  href="/forgot-password" 
                  className="text-sm text-blue-600 hover:text-blue-700"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </a>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Are you a partner?{' '}
                <a href="/partner/login" className="text-blue-600 hover:text-blue-700 font-medium">
                  Partner Portal
                </a>
              </p>
            </div>
            
            {/* Debug info - shows which backend the app is connecting to */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-400">
                API: {getActiveBackendUrl() || window.location.origin}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden bg-black"
      >
        <div className="noise-overlay"></div>
        <div className="relative z-10 text-white max-w-lg">
          <h2 className="text-4xl font-bold mb-4">{t('auth.welcome')}</h2>
          <p className="text-blue-200 text-lg leading-relaxed">
            {t('auth.manageTransfers')}
          </p>
        </div>
      </div>
    </div>
  );
}
